"use client";

import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useReducedMotion } from "motion/react";

gsap.registerPlugin(ScrollTrigger);

interface ProjectHeroImageProps {
  image?: string;
  /** Optional hero video. When set, `image` is used as its poster. */
  video?: string;
  alt: string;
}

const ProjectHeroImage: React.FC<ProjectHeroImageProps> = ({
  image,
  video,
  alt,
}) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
          scrollTrigger: {
            trigger: imageRef.current,
            start: "top 85%",
          },
        }
      );
    }, imageRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="pb-8">
      <div
        ref={imageRef}
        className={`w-full bg-[#f4f4f5] overflow-hidden ${
          video ? "aspect-video" : "h-[700px]"
        }`}
      >
        {video ? (
          <video
            src={video}
            poster={image}
            aria-label={alt}
            autoPlay={!shouldReduceMotion}
            loop
            muted
            playsInline
            preload="metadata"
            disablePictureInPicture
            className="pointer-events-none w-full h-full object-cover"
          />
        ) : image ? (
          <img
            src={image}
            alt={alt}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#f4f4f5]">
            <span className="text-gray-400 text-lg font-medium">{alt}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectHeroImage;
